import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Award, CheckCircle2, XCircle, Loader2, ExternalLink } from "lucide-react";
import { LOGO_URL } from "../constants";

const API = process.env.REACT_APP_BACKEND_URL || "";

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return d; }
};

export default function CertificateVerify() {
  const { certId } = useParams();
  const [cert,    setCert]    = useState(null);
  const [error,   setError]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/verify/${certId}`)
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then(setCert)
      .catch((e) => setError(e === 404 ? "Certificate not found or has been revoked." : "Verification service unavailable."))
      .finally(() => setLoading(false));
  }, [certId]);

  return (
    <div className="min-h-screen bg-[#0E0F12] text-white flex flex-col">
      {/* Header */}
      <header className="border-b border-white/[0.08] px-6 py-4 flex items-center gap-3">
        <div className="bg-black rounded-xl px-3 py-1.5">
          <img src={LOGO_URL} alt="Blue Mountain Group" className="h-8 w-auto" />
        </div>
        <div>
          <p className="text-xs text-gray-500">Certificate Verification</p>
          <p className="text-sm font-semibold text-white">Blue Mountain Academy</p>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-lg">
          {loading ? (
            <div className="flex flex-col items-center gap-3 text-gray-500">
              <Loader2 className="w-8 h-8 animate-spin" />
              <p className="text-sm">Verifying certificate…</p>
            </div>
          ) : error ? (
            <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-8 text-center">
              <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-red-400 mb-2">Not Verified</h2>
              <p className="text-sm text-gray-400">{error}</p>
              <p className="text-xs text-gray-600 mt-3 font-mono">{certId}</p>
            </div>
          ) : (
            <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden">
              {/* Status banner */}
              <div className="bg-[#CFFF50]/10 border-b border-[#CFFF50]/20 px-6 py-4 flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-[#CFFF50] shrink-0" />
                <div>
                  <p className="text-sm font-bold text-[#CFFF50]">Verified — Authentic Certificate</p>
                  <p className="text-xs text-gray-400 mt-0.5">This credential is confirmed in the Blue Mountain Academy records.</p>
                </div>
              </div>

              {/* Details */}
              <div className="px-6 py-6 space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#CFFF50]/10 flex items-center justify-center shrink-0">
                    <Award className="w-5 h-5 text-[#CFFF50]" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Certificate of Completion</p>
                    <p className="text-lg font-bold mt-0.5">{cert.course_title}</p>
                  </div>
                </div>

                <div className="border-t border-white/[0.06]" />

                {[
                  ["Awarded to",     cert.student_name],
                  ["Issued on",      fmtDate(cert.issued_at)],
                  ["Instructor",     cert.instructor || "Blue Mountain Group"],
                  ["Grade",          cert.grade],
                  ["Certificate ID", cert.code],
                ].filter(([, v]) => v).map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{label}</span>
                    <span className={`text-sm font-semibold ${label === "Grade" ? "text-[#CFFF50]" : "text-white"}`}>
                      {value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="px-6 pb-6">
                <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-white transition-colors">
                  <ExternalLink size={11} /> Blue Mountain Academy
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="text-center text-xs text-gray-700 py-4">
        © 2026 Blue Mountain Group · Certificate verification service
      </footer>
    </div>
  );
}
